import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSAVSecteur } from '../services/api';
import { useAuth } from '../services/AuthContext';
import BarreNav from '../components/BarreNav';

const TYPE_COLORS = {
  'RACC_I': { bg: 'var(--info-fond)', color: 'var(--accent)', border: 'var(--info-bord)' },
  'RACC_C': { bg: 'var(--info-fond)', color: 'var(--accent)', border: 'var(--info-bord)' },
  'RACC_F': { bg: 'var(--info-fond)', color: 'var(--accent)', border: 'var(--info-bord)' },
  'RACC_P': { bg: 'var(--info-fond)', color: 'var(--accent)', border: 'var(--info-bord)' },
  'Changement ONT': { bg: 'var(--attention-fond)', color: 'var(--attention-texte)', border: 'var(--attention-bord)' },
  'Raccordement': { bg: 'var(--info-fond)', color: 'var(--accent)', border: 'var(--info-bord)' },
};

function getTypeStyle(type) {
  return TYPE_COLORS[type] || { bg: 'var(--surface-3)', color: 'var(--texte-doux)', border: 'var(--bord)' };
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  const [y, m, d] = dateStr.split('-');
  if (!y || !m || !d) return dateStr;
  return `${d}/${m}/${y}`;
}

export default function SAVSecteur() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [interventions, setInterventions] = useState([]);
  const [secteur, setSecteur] = useState('');
  const [noSecteur, setNoSecteur] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastRefresh, setLastRefresh] = useState(null);

  const load = async () => {
    setLoading(true); setError('');
    try {
      const data = await getSAVSecteur();
      setInterventions(data.interventions || []);
      setSecteur(data.secteur || '');
      setNoSecteur(!!data.noSecteur);
      setLastRefresh(new Date());
    } catch {
      setError('Impossible de charger les SAV. Vérifiez votre connexion.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="app-shell">
      <div className="topbar">
        <div className="topbar-title">SAV Disponibles</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {lastRefresh && (
            <span style={{ fontSize: 11, color: 'var(--texte-doux)' }}>
              {lastRefresh.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
          <button
            onClick={load}
            disabled={loading}
            style={{ background: 'none', border: '1px solid var(--bord-fort)', borderRadius: 8, padding: '4px 10px', fontSize: 13, cursor: 'pointer', color: 'var(--accent)' }}
          >
            {loading ? '…' : '↻'}
          </button>
        </div>
      </div>

      <div className="content">

        {/* Secteur badge */}
        {secteur && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
            <span style={{ fontSize: 12, color: 'var(--texte-doux)' }}>Votre secteur :</span>
            <span style={{ fontSize: 13, fontWeight: 700, background: 'var(--info-fond)', color: 'var(--accent)', border: '1px solid var(--info-bord)', borderRadius: 20, padding: '3px 12px' }}>
              📍 {secteur}
            </span>
            {!loading && (
              <span style={{ fontSize: 12, color: 'var(--texte-doux)', marginLeft: 'auto' }}>
                {interventions.length} dispo{interventions.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>
        )}

        {/* No secteur */}
        {noSecteur && (
          <div style={{ background: 'var(--attention-fond)', border: '1px solid var(--attention-bord)', borderRadius: 12, padding: '14px 16px', marginBottom: 16, fontSize: 13, color: 'var(--attention-texte)' }}>
            ⚠️ Aucun secteur n'est configuré sur votre compte. Contactez un administrateur.
          </div>
        )}

        {/* Error */}
        {error && (
          <div style={{ background: 'var(--danger-fond)', border: '1px solid var(--danger-bord)', borderRadius: 12, padding: '14px 16px', marginBottom: 16, fontSize: 13, color: 'var(--danger-texte)' }}>
            {error}
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div style={{ textAlign: 'center', padding: 50, color: 'var(--texte-doux)' }}>Chargement…</div>
        )}

        {/* Empty */}
        {!loading && !error && !noSecteur && interventions.length === 0 && (
          <div style={{ textAlign: 'center', padding: 60, color: 'var(--texte-faible)' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>Aucun SAV disponible</div>
            <div style={{ fontSize: 13 }}>Aucune intervention à planifier dans votre secteur.</div>
          </div>
        )}

        {/* Liste */}
        {!loading && interventions.length > 0 && (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {interventions.map((item, i) => {
                const typeStyle = getTypeStyle(item.type_itv);
                return (
                  <div key={item.id || i} style={{
                    background: 'var(--surface)',
                    borderRadius: 14,
                    padding: '14px 16px',
                    border: '0.5px solid var(--bord)',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                  }}>
                    {/* Ligne 1 : réf WO + type */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6, gap: 8 }}>
                      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--texte)' }}>
                        {item.wo_reference || '—'}
                      </div>
                      {item.type_itv && (
                        <span style={{
                          fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 20,
                          background: typeStyle.bg, color: typeStyle.color, border: `1px solid ${typeStyle.border}`,
                          whiteSpace: 'nowrap', flexShrink: 0,
                        }}>
                          {item.type_itv}
                        </span>
                      )}
                    </div>

                    {/* Ligne 2 : adresse */}
                    <div style={{ fontSize: 12, color: 'var(--texte-doux)', marginBottom: 6 }}>
                      {[item.adresse, item.code_postal, item.ville].filter(Boolean).join(' ').toUpperCase()}
                    </div>

                    {/* Ligne 3 : motif + date déclenchement */}
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                      {item.motif && (
                        <span style={{ fontSize: 11, background: 'var(--surface-3)', color: 'var(--texte-doux)', borderRadius: 6, padding: '2px 8px' }}>
                          {item.motif}
                        </span>
                      )}
                      {item.date_declenchement && (
                        <span style={{ fontSize: 11, color: 'var(--texte-doux)', marginLeft: 'auto' }}>
                          Déclenchement : {formatDate(item.date_declenchement)}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Message d'information */}
            <div style={{
              marginTop: 20,
              background: 'var(--info-fond)',
              border: '1px solid var(--info-bord)',
              borderRadius: 12,
              padding: '14px 16px',
              fontSize: 13,
              color: 'var(--accent)',
              lineHeight: 1.6,
              textAlign: 'center',
            }}>
              📞 <strong>SAV disponibles dans votre secteur</strong><br />
              Pour récupérer l'une de ces interventions, contactez votre chargé d'affaires VALEA.
            </div>
          </>
        )}

        <div style={{ height: 16 }} />
      </div>

      <BarreNav actif="sav" />
    </div>
  );
}
